import { matchesKeyboardEvent, parseHotkey } from '@tanstack/hotkeys'
import { toDisposable } from './disposable'

const POINTER_TYPES = ['pointerdown', 'pointermove', 'pointerup', 'pointercancel']

function isEditableTarget(target) {
  if (!(target instanceof Element)) return false
  return (
    target.closest('input, textarea, select, [contenteditable]') !== null
  )
}

// Enter and Space on a focused control (a button, a link, a tab...) belong to
// that control: it is how a keyboard presses it. The board's own Enter
// ("write in the shape") and Space (pan) took them first and called
// preventDefault, so with Tab on "Create a new board" nothing happened, and a
// person using only the keyboard could not use the app at all (2026-09-26
// new-user test, #64). Other keys still reach the board.
function isControlActivation(raw) {
  if (raw.key !== 'Enter' && raw.key !== ' ') return false
  const t = raw.target
  if (!(t instanceof Element)) return false
  return (
    t.closest(
      'button, a[href], summary, [role="button"], [role="link"], [role="tab"], [role="menuitem"], [role="checkbox"], [role="switch"], [role="option"]'
    ) !== null
  )
}

// Interactive UI rendered inside the canvas (overlay buttons, panels) must
// receive normal clicks — the pipeline (and thus tools) leaves them alone.
// Plugins can opt any element out of canvas input with data-omni-nodrag.
function isUiTarget(target) {
  if (!(target instanceof Element)) return false
  return (
    target.closest(
      'button, a, input, textarea, select, [contenteditable], [data-omni-nodrag]'
    ) !== null
  )
}

// Keys are parsed and matched by @tanstack/hotkeys: 'mod' is ⌘ on a Mac and
// Ctrl elsewhere, and a Mac Option key that types a symbol (Option+L gives
// '¬', Option+E a dead key) still matches 'alt+l' by its physical key. The
// hand-made match here compared event.key, so every Option shortcut was dead
// on a Mac and five plugins listened for keydown themselves instead.
// A combo that cannot be parsed matches nothing rather than throwing.
function parseCombo(combo) {
  try {
    return parseHotkey(combo)
  } catch {
    return null
  }
}

// Single set of DOM listeners on the canvas root; plugins register handlers
// with a priority. Higher priority runs first; a handler may stop propagation
// or capture a pointerId so all later moves/ups go only to it.
export class EventPipeline {
  #handlers = new Map() // type -> array of {handler, priority, seq}
  // Observers see EVERY pointer event, captured or not, before any handler,
  // and cannot stop or capture it. Presence needs this: a tool that captures
  // the pointer used to hide every move from it, so the other person saw
  // your cursor frozen at the start of each drag (2026-09-25, bug 1).
  #observers = new Map() // type -> Set<handler>
  // every shortcut() call: { id, combo (as the plugin wrote it), parsed,
  // handler, keepDefault }. The id is '<plugin id>:<combo>', given by the
  // plugin context, so a person's keymap can name a binding without any
  // plugin knowing about keymaps.
  #bindings = new Set()
  #keymap = new Map() // id -> combo | null (null = no key)
  #bindingListeners = new Set()
  #captures = new Map() // pointerId -> { handler, paneId }
  #activePointers = new Map() // pointerId -> { screen, paneId }
  #seq = 0
  #roots = new Map() // paneId -> root element
  #cleanup = new Map() // paneId -> array of undo functions, plus 'window' for keyboard listeners
  #getWorld = new Map() // paneId -> converter function
  #activePaneId = 'main'
  #setActivePane = null // setter called by host on pointerdown

  setWorldConverter(fn, paneId) {
    this.#getWorld.set(paneId, fn)
    return toDisposable(() => {
      if (this.#getWorld.get(paneId) === fn) this.#getWorld.delete(paneId)
    })
  }

  setActivePaneSetter(fn) {
    this.#setActivePane = fn
    return toDisposable(() => {
      if (this.#setActivePane === fn) this.#setActivePane = null
    })
  }

  #ensureWindowListeners() {
    if (this.#cleanup.has('window')) return
    const cleanup = []
    const add = (target, type, fn, opts) => {
      target.addEventListener(type, fn, opts)
      cleanup.push(() => target.removeEventListener(type, fn, opts))
    }
    add(window, 'keydown', (e) => this.#dispatchKey('keydown', e))
    add(window, 'keyup', (e) => this.#dispatchKey('keyup', e))
    this.#cleanup.set('window', cleanup)
  }

  #removeWindowListeners() {
    const cleanup = this.#cleanup.get('window')
    if (cleanup) {
      for (const fn of cleanup) fn()
      this.#cleanup.delete('window')
    }
  }

  attach(rootEl, paneId) {
    // Detach only this pane if already attached
    if (this.#roots.has(paneId)) {
      this.detachPane(paneId)
    }
    this.#roots.set(paneId, rootEl)
    this.#ensureWindowListeners()

    const cleanup = []
    const add = (target, type, fn, opts) => {
      target.addEventListener(type, fn, opts)
      cleanup.push(() => target.removeEventListener(type, fn, opts))
    }
    for (const type of POINTER_TYPES) {
      add(rootEl, type, (e) => this.#dispatchPointer(type, e, paneId))
    }
    add(rootEl, 'wheel', (e) => this.#dispatchWheel(e, paneId), { passive: false })
    this.#cleanup.set(paneId, cleanup)
  }

  detachPane(paneId) {
    const cleanup = this.#cleanup.get(paneId)
    if (cleanup) {
      for (const fn of cleanup) fn()
      this.#cleanup.delete(paneId)
    }
    this.#roots.delete(paneId)
    // Drop captures and active pointers belonging to this pane
    for (const [id, cap] of this.#captures) {
      if (cap.paneId === paneId) this.#captures.delete(id)
    }
    for (const [id, ptr] of this.#activePointers) {
      if (ptr.paneId === paneId) this.#activePointers.delete(id)
    }
    // If this was the last pane, remove window listeners
    if (this.#roots.size === 0) {
      this.#removeWindowListeners()
    }
  }

  detach() {
    for (const paneId of this.#roots.keys()) {
      this.detachPane(paneId)
    }
    this.#getWorld.clear()
    this.#activePaneId = 'main'
  }

  on(type, handler, { priority = 0, observe = false } = {}) {
    if (observe) {
      let set = this.#observers.get(type)
      if (!set) {
        set = new Set()
        this.#observers.set(type, set)
      }
      set.add(handler)
      return toDisposable(() => set.delete(handler))
    }
    let list = this.#handlers.get(type)
    if (!list) {
      list = []
      this.#handlers.set(type, list)
    }
    const entry = { handler, priority, seq: ++this.#seq }
    list.push(entry)
    // higher priority first; stable by registration order
    list.sort((a, b) => b.priority - a.priority || a.seq - b.seq)
    return toDisposable(() => {
      const i = list.indexOf(entry)
      if (i >= 0) list.splice(i, 1)
      // a removed handler must not keep pointer captures alive
      for (const [id, h] of this.#captures) {
        if (h.handler === handler) this.#captures.delete(id)
      }
    })
  }

  // { preventDefault: false } leaves the key's default action alone - ⌘C /
  // ⌘X / ⌘V then still fire the browser's own copy / cut / paste events,
  // which carry the clipboard with no permission (omni.clipboard, bug 5).
  // A combo's default is kept only if EVERY handler for it asks for that.
  // { preventDefault: false } leaves the key's default action alone - ⌘C /
  // ⌘X / ⌘V then still fire the browser's own copy / cut / paste events,
  // which carry the clipboard with no permission (omni.clipboard, bug 5).
  // A key's default is kept only if EVERY binding it matches asks for that.
  shortcut(combo, handler, { preventDefault = true, id } = {}) {
    const b = { id: id ?? combo.toLowerCase(), combo, handler, keepDefault: !preventDefault }
    b.parsed = parseCombo(this.#comboOf(b))
    this.#bindings.add(b)
    this.#notifyBindings()
    return toDisposable(() => {
      if (this.#bindings.delete(b)) this.#notifyBindings()
    })
  }

  #comboOf(b) {
    return this.#keymap.has(b.id) ? this.#keymap.get(b.id) : b.combo
  }

  // The live bindings, one row per id (several handlers may share an id, e.g.
  // Escape bound by two tools through the toolbar): { id, combo, defaultCombo }
  listShortcuts() {
    const rows = new Map()
    for (const b of this.#bindings) {
      if (!rows.has(b.id)) rows.set(b.id, { id: b.id, combo: this.#comboOf(b), defaultCombo: b.combo })
    }
    return [...rows.values()]
  }

  // A person's keymap: { [id]: combo | null }. Replaces the previous one;
  // an id not named goes back to its default. Applies to live bindings at
  // once and to bindings made later.
  setKeymap(map) {
    this.#keymap = new Map(Object.entries(map ?? {}))
    for (const b of this.#bindings) b.parsed = parseCombo(this.#comboOf(b))
    this.#notifyBindings()
  }

  onShortcutsChange(cb) {
    this.#bindingListeners.add(cb)
    return toDisposable(() => this.#bindingListeners.delete(cb))
  }

  #notifyBindings() {
    for (const cb of [...this.#bindingListeners]) {
      try {
        cb()
      } catch (err) {
        console.error('[omni] shortcuts listener failed', err)
      }
    }
  }

  releasePointer(pointerId) {
    this.#captures.delete(pointerId)
  }

  getActivePointers() {
    // Return shape compatible with existing callers: pointerId -> { screen }
    const result = new Map()
    for (const [id, ptr] of this.#activePointers) {
      result.set(id, { screen: ptr.screen })
    }
    return result
  }

  #screenPos(e, rootEl) {
    const rect = rootEl.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  #makePointerEvent(raw, screen, paneId) {
    const pipeline = this
    let stopped = false
    const converter = this.#getWorld.get(paneId)
    const ev = {
      raw,
      screen,
      paneId,
      get world() {
        return converter ? converter(screen) : undefined
      },
      stopPropagation() {
        stopped = true
      },
      capturePointer() {
        pipeline.#captures.set(raw.pointerId, { handler: ev._currentHandler, paneId })
        try {
          const root = pipeline.#roots.get(paneId)
          if (root) root.setPointerCapture(raw.pointerId)
        } catch {
          // capture can fail if the pointer is already gone — harmless
        }
      },
      isStopped: () => stopped,
    }
    return ev
  }

  #dispatchPointer(type, raw, paneId) {
    // a press on overlay UI is not canvas input — but captured streams and
    // releases still flow so tools never get stuck mid-gesture
    if (
      type === 'pointerdown' &&
      !this.#captures.has(raw.pointerId) &&
      isUiTarget(raw.target)
    ) {
      return
    }
    const rootEl = this.#roots.get(paneId)
    if (!rootEl) return
    const screen = this.#screenPos(raw, rootEl)
    if (type === 'pointerdown' || type === 'pointermove') {
      this.#activePointers.set(raw.pointerId, { screen, paneId })
    }
    const ev = this.#makePointerEvent(raw, screen, paneId)

    // Activation: on pointerdown, BEFORE running any handler, tell the kernel
    // that this pane is now active.
    if (type === 'pointerdown' && this.#setActivePane) {
      this.#setActivePane(paneId)
      this.#activePaneId = paneId
    }

    for (const observer of [...(this.#observers.get(type) ?? [])]) {
      try {
        observer({ raw: ev.raw, screen: ev.screen, paneId: ev.paneId, world: ev.world })
      } catch (err) {
        console.error('[omni] pointer observer failed', err)
      }
    }

    const captured = this.#captures.get(raw.pointerId)
    if (captured && type !== 'pointerdown') {
      ev._currentHandler = captured.handler
      try {
        captured.handler(ev)
      } catch (err) {
        console.error('[omni] pointer capture handler failed', err)
      }
    } else {
      for (const entry of [...(this.#handlers.get(type) ?? [])]) {
        ev._currentHandler = entry.handler
        try {
          entry.handler(ev)
        } catch (err) {
          console.error('[omni] pointer handler failed', err)
        }
        if (ev.isStopped()) break
      }
    }

    if (type === 'pointerup' || type === 'pointercancel') {
      this.#captures.delete(raw.pointerId)
      this.#activePointers.delete(raw.pointerId)
    }
  }

  #dispatchWheel(raw, paneId) {
    raw.preventDefault() // never let the browser zoom/scroll the page
    const rootEl = this.#roots.get(paneId)
    if (!rootEl) return
    const screen = this.#screenPos(raw, rootEl)
    let stopped = false
    const converter = this.#getWorld.get(paneId)
    const ev = {
      raw,
      screen,
      paneId,
      get world() {
        return converter ? converter(screen) : undefined
      },
      stopPropagation() {
        stopped = true
      },
    }
    for (const entry of [...(this.#handlers.get('wheel') ?? [])]) {
      try {
        entry.handler(ev)
      } catch (err) {
        console.error('[omni] wheel handler failed', err)
      }
      if (stopped) break
    }
  }

  #dispatchKey(type, raw) {
    if (isEditableTarget(raw.target)) return
    if (isControlActivation(raw)) return
    if (type === 'keydown') {
      const hits = [...this.#bindings].filter((b) => b.parsed && matchesKeyboardEvent(raw, b.parsed))
      if (hits.length) {
        if (!hits.every((b) => b.keepDefault)) raw.preventDefault()
        // '?' and 'shift+?' both match Shift+/ now: a handler bound to both
        // runs once, or a toggle would open and close in the same key press
        for (const handler of new Set(hits.map((b) => b.handler))) {
          try {
            handler()
          } catch (err) {
            console.error('[omni] shortcut handler failed', err)
          }
        }
        return
      }
    }
    let stopped = false
    const ev = {
      raw,
      paneId: this.#activePaneId,
      stopPropagation() {
        stopped = true
      },
    }
    for (const entry of [...(this.#handlers.get(type) ?? [])]) {
      try {
        entry.handler(ev)
      } catch (err) {
        console.error('[omni] key handler failed', err)
      }
      if (stopped) break
    }
  }
}